import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react'
import { useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { KeyboardEvent } from 'react'
import type { ConsentValue } from '@/lib/consent'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { designSystem } from '@/lib/design-system'
import {
  getConsent,
  onConsentChange,
  onCookieSettingsOpen,
  setConsent,
} from '@/lib/consent'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'

/**
 * English fallbacks. Translators add common.consent.* to the non-priority
 * locales separately, and i18next loads only the current locale (no fallback
 * bundle on the client), so a missing key must never render as the raw key.
 */
const FALLBACK = {
  title: 'Analytics cookies',
  body: "We'd like to use third-party analytics to see how the site is used. They only load if you agree, and our privacy policy names the processors.",
  accept: 'Accept',
  decline: 'Decline',
  privacy: 'Privacy policy',
} as const

// ─── "Is the banner open?" store ────────────────────────────────────────────
// On phones the banner docks where the bottom navigation sits. Server and
// first client render: closed, so the SSR markup never depends on it.

let bannerOpen = false
const bannerListeners = new Set<() => void>()

function setBannerOpen(open: boolean): void {
  if (bannerOpen === open) return
  bannerOpen = open
  for (const listener of bannerListeners) listener()
}

function subscribeBanner(listener: () => void): () => void {
  bannerListeners.add(listener)
  return () => bannerListeners.delete(listener)
}

/** True while the cookie banner is on screen. Always false during SSR. */
export function useConsentBannerOpen(): boolean {
  return useSyncExternalStore(
    subscribeBanner,
    () => bannerOpen,
    () => false,
  )
}

/**
 * Analytics cookie banner (decision D4). Non-blocking and fixed to the bottom
 * of the viewport, so it never shifts page content. Shown when the visitor
 * has not decided yet, or when openCookieSettings() is called (footer
 * "Cookie settings" button). Renders nothing on the server and on the first
 * client render, so hydration always matches the SSR markup.
 *
 * Phones get a compact bar docked in place of the bottom navigation; from
 * `md` up it is a card in the bottom-right corner. Both choices always have
 * equal weight.
 */
export function CookieConsent() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)
  const titleId = useId()
  const textId = useId()

  const [open, setOpen] = useState(false)
  const [openedOnRequest, setOpenedOnRequest] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (getConsent() === null) setOpen(true)

    const offOpen = onCookieSettingsOpen(() => {
      const active = document.activeElement
      returnFocusRef.current = active instanceof HTMLElement ? active : null
      setOpenedOnRequest(true)
      setOpen(true)
    })
    // A decision made anywhere (this banner or another instance) closes it.
    const offChange = onConsentChange(() => setOpen(false))

    return () => {
      offOpen()
      offChange()
    }
  }, [])

  useEffect(() => {
    setBannerOpen(open)
    return () => setBannerOpen(false)
  }, [open])

  // Opened from "Cookie settings": move focus in so keyboard users land on it.
  useEffect(() => {
    if (open && openedOnRequest) dialogRef.current?.focus()
  }, [open, openedOnRequest])

  const close = () => {
    setOpen(false)
    if (openedOnRequest) {
      const target = returnFocusRef.current
      returnFocusRef.current = null
      if (target?.isConnected) target.focus({ preventScroll: true })
    }
    setOpenedOnRequest(false)
  }

  const decide = (value: ConsentValue) => {
    setConsent(value)
    close()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Escape dismisses only a re-opened banner; a first-time visitor must choose.
    if (event.key === 'Escape' && getConsent() !== null) {
      event.stopPropagation()
      close()
    }
  }

  if (!open) return null

  const title = t('consent.title', { defaultValue: FALLBACK.title })
  const body = t('consent.body', { defaultValue: FALLBACK.body })
  const privacyHref = withLocalePath('/privacy', locale) + '#cookies'

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="false"
      aria-live="polite"
      aria-labelledby={titleId}
      aria-describedby={textId}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      data-testid="cookie-consent"
      className={[
        // Phones: a compact bar docked at the bottom edge, where the bottom
        // navigation sits (the nav hides while this is open).
        'fixed inset-x-0 bottom-0 z-[55] rounded-t-2xl border-t border-border bg-background px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-foreground shadow-[0_-8px_30px_rgba(15,23,42,0.16)] outline-none',
        'animate-in fade-in slide-in-from-bottom-2 duration-300 motion-reduce:animate-none focus-visible:ring-2 focus-visible:ring-ring print:hidden',
        // md+: a card in the bottom-right corner, 6rem up: above the chat
        // launcher (bottom-6, 3.5rem tall) and the hero carousel controls
        // (md:pb-10), so neither is covered while the visitor decides.
        'md:inset-x-auto md:right-6 md:bottom-24 md:w-full md:max-w-md md:rounded-2xl md:border md:p-5 md:shadow-[0_10px_35px_rgba(15,23,42,0.18)]',
      ].join(' ')}
    >
      <p
        id={titleId}
        className={cn('font-semibold text-foreground', designSystem.typography.body.base)}
      >
        {title}
      </p>
      <p
        id={textId}
        className={cn('mt-1.5', designSystem.typography.body.small, designSystem.typography.muted)}
      >
        {body}{' '}
        <a
          href={privacyHref}
          className={cn(
            'font-medium text-primary underline underline-offset-4 transition-colors hover:text-primary/80',
            designSystem.effects.focusRing,
          )}
        >
          {t('consent.privacy', { defaultValue: FALLBACK.privacy })}
        </a>
      </p>
      {/* Equal weight for both choices: declining is as easy as accepting. */}
      <div className="mt-2.5 grid grid-cols-2 gap-2 md:mt-4">
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full"
          onClick={() => decide('denied')}
        >
          {t('consent.decline', { defaultValue: FALLBACK.decline })}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full"
          onClick={() => decide('granted')}
        >
          {t('consent.accept', { defaultValue: FALLBACK.accept })}
        </Button>
      </div>
    </div>
  )
}
